// Maison Dodji — i18n
// French is the master reference. English is an editorial adaptation,
// not a literal translation. All copy below is approved.

export const translations = {
  fr: {
    locale: "fr-FR",

    nav: {
      maison: "Maison",
      collection: "Collection",
      univers: "Univers",
      contact: "Présentation privée",
    },

    cta: {
      eyebrow: "— Présentation privée",
      titleDefault: "Reconnaître la pièce qui vous ressemble.",
      titleContact: "Une rencontre, un lieu, une pièce.",
      body:
        "Maison Dodji privilégie une relation directe, fondée sur la présentation des pièces, la compréhension des lieux et la qualité des échanges. La maison reçoit sur rendez-vous.",
      button: "Demander une présentation privée",
    },

    footer: {
      brand: "Maison Dodji",
      tagline:
        "Meubles singuliers pour montres automatiques. Une maison parisienne dédiée à la présence de l'objet horloger dans l'espace.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      copyright: "© {year} Maison Dodji — Tous droits réservés",
      baseline: "Meubles singuliers pour montres automatiques",
    },

    home: {
      eyebrowYear: "— 2026",
      eyebrowEdition: "Édition régulière · Paris",
      title: "Maison\u00A0Dodji",
      tagline: "Meubles singuliers pour montres automatiques.",
      intro:
        "Maison Dodji compose des objets de présence à la frontière du design, du mobilier et de l'horlogerie. Pensée comme une collection de formes à habiter, la maison invite chacun à reconnaître la pièce qui lui ressemble.",
      ctaCollection: "Découvrir la collection",
      ctaContact: "Présentation privée",
      featureEyebrow: "— Pièce signature",
      featureCta: "Découvrir",

      statementEyebrow: "01 — La maison",
      statementTitle:
        "Donner à la montre automatique sa juste place dans l'espace.",
      statementBody:
        "Maison Dodji ne se contente pas d'accompagner le mouvement horloger. La maison donne à la montre automatique une présence singulière dans le lieu qui l'accueille — un meuble plutôt qu'un accessoire, une silhouette plutôt qu'un rangement.",
      statementCta: "Lire la maison",

      collectionEyebrow: "02 — La collection",
      collectionTitleLine1: "Cinq modèles,",
      collectionTitleLine2: "cinq présences,",
      collectionTitleLine3:
        "cinq manières d'inscrire le mouvement dans l'espace.",
      collectionAside:
        "Maison Dodji ne juxtapose pas des objets\u00A0; la maison construit un langage. Chaque pièce possède son rythme, sa silhouette et son intensité.",

      featuredEyebrow: "— Pièces choisies",
      featuredAll: "Les cinq pièces",
      featuredCta: "Découvrir",

      universeEyebrow: "03 — L'univers",
      universeTitle:
        "Une grammaire d'objets pensés pour durer, pour habiter, pour transmettre.",
      universePillars: [
        ["Présence", "ce qui occupe le lieu sans le saturer"],
        ["Forme", "la silhouette comme premier langage"],
        ["Silhouette", "une lecture immédiate, calme"],
        ["Rythme", "la cadence qui structure l'espace"],
        ["Résonance", "l'objet qui répond à l'intérieur"],
        ["Transmission", "l'objet que l'on lègue"],
      ],
      universeCta: "Explorer l'univers",

      placesEyebrow: "04 — Lieux",
      placesTitle:
        "Pensés pour les intérieurs assumés, les bureaux silencieux, les lieux où l'objet se choisit.",
      placesBlocks: [
        {
          eyebrow: "Fabrication",
          body:
            "Atelier français — bois laqué, métal gravé, modules mécaniques suisses.",
        },
        {
          eyebrow: "Distribution",
          body:
            "Sur rendez-vous, sur invitation. Présentation privée uniquement.",
        },
        {
          eyebrow: "Engagement",
          body:
            "Édition régulière, série limitée à la demande, suivi par la maison.",
        },
      ],
    },

    collection: {
      eyebrow: "— La collection",
      title: "Cinq pièces, un langage.",
      intro:
        "Cinq modèles, cinq présences, cinq manières d'inscrire le mouvement horloger dans l'espace. Maison Dodji ne juxtapose pas des objets\u00A0; la maison construit un langage. Chaque pièce possède son rythme, sa silhouette et son intensité.",
      asideLine1: "Prix de collection.",
      asideLine2: "Présentation privée sur rendez-vous.",
      renderingNote:
        "Les visuels présentés ont été volontairement réalisés comme des rendus de projection, afin d'exprimer la dimension personnalisable de chaque pièce.",
      pieceLabel: "Pièce N°",
      pieceCount: (n) => `${n} montre${n > 1 ? "s" : ""}`,
      cta: "Découvrir",
    },

    product: {
      back: "← Collection",
      pieceLabel: "Pièce N°",
      specsTitle: "Caractéristiques",
      specOrdinal: (i) => `0${i + 1}`,
      priceLabel: "Prix conseillé",
      editionLabel: "Édition régulière · Sur rendez-vous",
      ctaContact: "Demander une présentation",
      ctaCollection: "Voir la collection",
      otherEyebrow: "— Autres pièces",
      otherTitle: "Reconnaître la pièce qui vous ressemble.",
      linkAll: "Toute la collection",
    },

    maison: {
      eyebrow: "— La maison",
      title: "Une maison parisienne dédiée à la présence horlogère.",
      intro1:
        "Maison Dodji est née à Paris d'une intuition simple\u00A0: la montre automatique mérite davantage qu'un rangement. Elle mérite un lieu, une silhouette, un meuble qui lui réponde.",
      intro2:
        "À la frontière du design, du mobilier et de l'horlogerie, la maison conçoit des pièces singulières — pensées pour habiter l'espace plutôt que pour s'y dissimuler. Chaque modèle est une forme à reconnaître, une présence à accueillir, un rythme à installer.",
      introAside:
        "Choisir une pièce Maison Dodji, ce n'est pas posséder un objet de plus. C'est reconnaître une forme, accepter un rythme, faire place à une présence.",
      gestureEyebrow: "— Le geste",
      gestureTitle: "Habiter l'objet, plutôt que le posséder.",
      gestureBody:
        "Chaque pièce est conçue comme une forme à habiter\u00A0: un volume qui structure le lieu, une matière qui dialogue avec la lumière, un mouvement intérieur qui devient présence.",
      pillarsEyebrow: "— Trois axes",
      pillarsTitle: "Trois axes structurent la maison.",
      pillars: [
        {
          eyebrow: "Design",
          title: "Forme et silhouette",
          body:
            "Des pièces dessinées pour la lecture immédiate, pour la justesse du volume, pour la précision du détail.",
        },
        {
          eyebrow: "Mobilier",
          title: "Présence et lieu",
          body:
            "Un meuble singulier qui ne se contente pas d'accompagner&nbsp;: il structure, il habite, il s'installe.",
        },
        {
          eyebrow: "Horlogerie",
          title: "Mouvement et durée",
          body:
            "Des modules mécaniques suisses au service de la montre mécanique, dans un écrin pensé pour la durée.",
        },
      ],
      transmissionEyebrow: "— Transmission",
      transmissionTitle:
        "Un objet pensé pour vivre, pour durer, pour se transmettre.",
      transmissionBody:
        "Chaque pièce Maison Dodji est conçue pour traverser le temps — non comme un produit qui s'use, mais comme un meuble qui s'installe, qui accompagne, qui se transmet. Le mouvement automatique trouve sa place\u00A0; la maison trouve la sienne.",
      transmissionCta: "Voir la collection",
      signatureAlt: "Lumumba — pièce Maison Dodji",
    },

    univers: {
      eyebrow: "— L'univers",
      title: "Une maison, un langage, un monde.",
      doctrine:
        "Maison Dodji conçoit des pièces de mobilier d'usage où la montre mécanique devient présence, rituel et objet d'intérieur.",
      intro:
        "L'univers Maison Dodji se lit dans la matière, dans la silhouette, dans la manière dont la pièce s'inscrit dans le lieu. La maison s'adresse à ceux qui considèrent l'objet horloger comme une présence — non comme un accessoire — et qui souhaitent l'accueillir dans un meuble pensé pour cela.",
      introAsideL1: "Le mobilier comme cadre.",
      introAsideL2: "Le mouvement comme rythme.",
      introAsideL3: "Le lieu comme résonance.",
      markersEyebrow: "— Repères",
      markersTitle: "Là où la maison s'inscrit.",
      markers: [
        {
          num: "01",
          title: "Design",
          body:
            "Une recherche de la silhouette juste — sculpturale sans démonstration, présente sans saturation.",
        },
        {
          num: "02",
          title: "Mobilier",
          body:
            "Un objet pensé comme un meuble&nbsp;: assise, présence, durée. Une pièce du lieu, et non un accessoire.",
        },
        {
          num: "03",
          title: "Horlogerie",
          body:
            "Le mouvement mécanique au cœur de l'objet&nbsp;: modules mécaniques suisses, montres automatiques, mouvement continu.",
        },
        {
          num: "04",
          title: "Intérieurs",
          body:
            "Bureaux silencieux, salons composés, intérieurs privés, lieux où chaque objet est choisi avec l'exigence d'une œuvre.",
        },
      ],
      collabEyebrow: "— Collaborations",
      collabTitle: "Présentations, lieux, dialogues.",
      collabBody1:
        "Maison Dodji accueille un nombre restreint de collaborations avec des architectes d'intérieur, des décorateurs, des lieux privés, des projets résidentiels d'exception, des artistes et des galeries choisies comme espaces de mise en scène. Certains acteurs horlogers peuvent également être associés lorsque le dialogue respecte l'esprit de la maison. Chaque dialogue se construit dans la durée, autour de la pièce — non autour de la transaction.",
      collabBody2:
        "Les présentations privées se déroulent dans des lieux dédiés ou directement au sein des intérieurs des clients. La maison se déplace lorsque la pièce le justifie.",
      collabSpacesEyebrow: "— Espaces de collaboration",
      collabSpacesTitle: "Mises en scène privées.",
      collabSpacesBody:
        "Maison Dodji ouvre un espace de collaboration avec des architectes d'intérieur, décorateurs, galeries et lieux privés afin de mettre en scène ses pièces dans des environnements plus denses, plus habités, où le mobilier horloger dialogue avec les matières, les œuvres, la lumière et l'usage.",
      stagingNoteEyebrow: "— Lumière à venir",
      stagingNote:
        "Les visuels actuels présentent les pièces dans une lumière volontairement neutre. Les collaborations à venir permettront de les inscrire dans des intérieurs plus denses, afin d'exprimer leur rapport aux matières, aux œuvres, aux meubles et aux lieux.",
      locations: [
        ["Sur rendez-vous", "Présentations privées"],
        ["Sur déplacement", "Selon le projet"],
        ["Sur invitation", "Édition régulière"],
      ],
      imageAlts: {
        sankara: "Sankara — présence horizontale",
        rosa: "Rosa — pièce circulaire",
        quincy: "Quincy — densité du quatuor",
      },
    },

    contact: {
      eyebrow: "— Présentation privée",
      byAppointment: "Sur rendez-vous",
      title: "Écrire à la Maison",
      intro:
        "Pour toute demande de présentation privée, d'acquisition ou de collaboration, merci d'écrire à la Maison via le formulaire ci-dessous ou à l'adresse suivante\u00A0:",
      asideL1: "Une relation,",
      asideL2: "non une transaction.",

      processEyebrow: "— Le déroulé",
      steps: [
        {
          num: "01",
          title: "Échange initial",
          body:
            "Nous vous répondons sous 48 heures. Un premier échange permet de comprendre votre lieu, votre intention et la pièce qui vous intéresse.",
        },
        {
          num: "02",
          title: "Présentation privée",
          body:
            "Sur rendez-vous, dans des lieux dédiés ou directement chez vous lorsque le projet le justifie. La pièce est présentée dans son contexte d'accueil.",
        },
        {
          num: "03",
          title: "Suivi de la maison",
          body:
            "Chaque pièce est conçue, livrée et accompagnée dans la durée. La maison reste votre interlocuteur unique.",
        },
      ],

      asideEyebrow: "— Contact",
      writeEyebrow: "Écrire à la maison",

      form: {
        nom: "Nom",
        nomPlaceholder: "Votre nom",
        email: "Email",
        emailPlaceholder: "vous@adresse.com",
        ville: "Ville / Pays",
        villePlaceholder: "Paris, France",
        objet: "Objet de la demande",
        piece: "Pièce d'intérêt",
        pieceNoPreference: "— Aucune préférence",
        message: "Message",
        messagePlaceholder:
          "Quelques mots sur votre projet, votre lieu, votre intention…",
        note: "Nous reviendrons vers vous dans les meilleurs délais.",
        submit: "Envoyer la demande",
        submitting: "Envoi…",
      },
      objects: [
        "Présentation privée",
        "Demande d'information",
        "Projet spécifique",
        "Collaboration",
      ],

      successEyebrow: "— Demande reçue",
      successTitle: "Merci. La maison vous répondra prochainement.",
      successBody:
        "Votre demande a bien été transmise à Maison Dodji. Nous reviendrons vers vous dans les meilleurs délais pour convenir d'une présentation privée.",

      toastSuccess: "Votre demande a bien été reçue par la maison.",
      toastErrorRequired: "Merci de renseigner les informations essentielles.",
      toastErrorGeneric: "Une erreur est survenue. Merci de réessayer.",

      closingEyebrow: "— Maison Dodji",
      closingTitle: "Meubles singuliers pour montres automatiques.",
      closingBody:
        "Sur rendez-vous. La maison se déplace lorsque la pièce le justifie.",
    },
  },

  en: {
    locale: "en-GB",

    nav: {
      maison: "Maison",
      collection: "Collection",
      univers: "Univers",
      contact: "Private Presentation",
    },

    cta: {
      eyebrow: "— Private Presentation",
      titleDefault: "Recognize the piece that feels like you.",
      titleContact: "A meeting, a place, a piece.",
      body:
        "Maison Dodji favors a direct relationship, grounded in the presentation of the pieces, the understanding of places, and the quality of exchange. The maison welcomes inquiries by appointment.",
      button: "Request a private presentation",
    },

    footer: {
      brand: "Maison Dodji",
      tagline:
        "Singular furniture pieces for automatic watches. A Parisian maison devoted to the presence of the horological object in space.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      copyright: "© {year} Maison Dodji — All rights reserved",
      baseline: "Singular furniture pieces for automatic watches",
    },

    home: {
      eyebrowYear: "— 2026",
      eyebrowEdition: "Regular edition · Paris",
      title: "Maison\u00A0Dodji",
      tagline: "Singular furniture pieces for automatic watches.",
      intro:
        "Maison Dodji creates objects of presence at the intersection of design, furniture and horology. Conceived as a collection of forms to inhabit, the maison invites each person to recognize the piece that feels like their own.",
      ctaCollection: "Discover the collection",
      ctaContact: "Request a private presentation",
      featureEyebrow: "— Signature piece",
      featureCta: "Discover",

      statementEyebrow: "01 — The maison",
      statementTitle:
        "Giving the automatic watch its rightful place in space.",
      statementBody:
        "Maison Dodji does not merely accompany the movement of the watch. The maison gives the automatic watch a singular presence within the space that receives it — a piece of furniture rather than an accessory, a silhouette rather than an object of storage.",
      statementCta: "Life at the maison",

      collectionEyebrow: "02 — The collection",
      collectionTitleLine1: "Five models,",
      collectionTitleLine2: "five presences,",
      collectionTitleLine3:
        "five ways of inscribing movement into space.",
      collectionAside:
        "Maison Dodji does not juxtapose objects; the maison constructs a language. Each piece carries its own rhythm, silhouette and intensity.",

      featuredEyebrow: "— Selected pieces",
      featuredAll: "All five pieces",
      featuredCta: "Discover",

      universeEyebrow: "03 — Universe",
      universeTitle:
        "A grammar of objects conceived to endure, to inhabit, to transmit.",
      universePillars: [
        ["Presence", "what occupies a place without saturating it"],
        ["Form", "silhouette as a first language"],
        ["Silhouette", "an immediate, quiet reading"],
        ["Rhythm", "the cadence that structures space"],
        ["Resonance", "the object that responds to the interior"],
        ["Transmission", "the object one passes on"],
      ],
      universeCta: "Explore the universe",

      placesEyebrow: "04 — Places",
      placesTitle:
        "Conceived for assured interiors, quiet offices, and the places where an object is chosen.",
      placesBlocks: [
        {
          eyebrow: "Craftsmanship",
          body:
            "French workshop — lacquered wood, engraved metal, Swiss mechanical modules.",
        },
        {
          eyebrow: "Presentation",
          body:
            "By appointment, on invitation. Private presentation only.",
        },
        {
          eyebrow: "Commitment",
          body:
            "Regular edition, limited production on demand, followed by the maison.",
        },
      ],
    },

    collection: {
      eyebrow: "— The collection",
      title: "Five pieces, one language.",
      intro:
        "Five models, five presences, five ways of inscribing movement into space. Maison Dodji does not juxtapose objects; the maison constructs a language. Each piece carries its own rhythm, silhouette and intensity.",
      asideLine1: "Collection prices.",
      asideLine2: "Private presentation by appointment.",
      renderingNote:
        "The visuals presented were intentionally developed as projection renderings, designed to express the customizable nature of each piece.",
      pieceLabel: "Piece N°",
      pieceCount: (n) => `${n} watch${n > 1 ? "es" : ""}`,
      cta: "Discover",
    },

    product: {
      back: "← Collection",
      pieceLabel: "Piece N°",
      specsTitle: "Specifications",
      specOrdinal: (i) => `0${i + 1}`,
      priceLabel: "Recommended price",
      editionLabel: "Regular edition · By appointment",
      ctaContact: "Request a presentation",
      ctaCollection: "View the collection",
      otherEyebrow: "— Other pieces",
      otherTitle: "Recognize the piece that feels like you.",
      linkAll: "The full collection",
    },

    maison: {
      eyebrow: "— The maison",
      title: "A Parisian maison devoted to horological presence.",
      intro1:
        "Maison Dodji was born in Paris from a simple intuition: the automatic watch deserves more than mere storage. It deserves a place, a silhouette, a piece of furniture that answers it.",
      intro2:
        "At the intersection of design, furniture and horology, the maison conceives singular pieces — meant to inhabit space rather than to disappear within it. Each model is a form to recognize, a presence to welcome, a rhythm to settle in.",
      introAside:
        "To choose a Maison Dodji piece is not to own one more object. It is to recognize a form, to accept a rhythm, to make room for a presence.",
      gestureEyebrow: "— The gesture",
      gestureTitle: "To inhabit the object, rather than to own it.",
      gestureBody:
        "Each piece is conceived as a form to inhabit: a volume that structures a place, a material in dialogue with light, an inner movement that becomes presence.",
      pillarsEyebrow: "— Three axes",
      pillarsTitle: "Three axes structure the maison.",
      pillars: [
        {
          eyebrow: "Design",
          title: "Form and silhouette",
          body:
            "Pieces drawn for immediate reading, for the rightness of volume, for the precision of detail.",
        },
        {
          eyebrow: "Furniture",
          title: "Presence and place",
          body:
            "A singular piece of furniture that does not simply accompany&nbsp;: it structures, it inhabits, it settles in.",
        },
        {
          eyebrow: "Horology",
          title: "Movement and time",
          body:
            "Swiss mechanical modules in service of the mechanical watch, within a setting conceived to last.",
        },
      ],
      transmissionEyebrow: "— Transmission",
      transmissionTitle:
        "An object conceived to live, to endure, to be passed on.",
      transmissionBody:
        "Each Maison Dodji piece is made to cross time — not as a product that wears out, but as a piece of furniture that settles in, that accompanies, that is passed on. The automatic movement finds its place; the maison finds its own.",
      transmissionCta: "View the collection",
      signatureAlt: "Lumumba — Maison Dodji piece",
    },

    univers: {
      eyebrow: "— Universe",
      title: "One maison, one language, one world.",
      doctrine:
        "Maison Dodji conceives pieces of useful furniture in which the mechanical watch becomes presence, ritual and interior object.",
      intro:
        "The Maison Dodji universe is read in matter, in silhouette, in the way each piece takes its place within a space. The maison addresses those who consider the horological object a presence — not an accessory — and who wish to receive it within a piece of furniture conceived for that purpose.",
      introAsideL1: "Furniture as frame.",
      introAsideL2: "Movement as rhythm.",
      introAsideL3: "Place as resonance.",
      markersEyebrow: "— Coordinates",
      markersTitle: "Where the maison takes its place.",
      markers: [
        {
          num: "01",
          title: "Design",
          body:
            "A search for the right silhouette — sculptural without demonstration, present without saturation.",
        },
        {
          num: "02",
          title: "Furniture",
          body:
            "An object conceived as furniture&nbsp;: base, presence, duration. A piece of place, not an accessory.",
        },
        {
          num: "03",
          title: "Horology",
          body:
            "Mechanical movement at the heart of the object&nbsp;: Swiss mechanical modules, automatic watches, continuous movement.",
        },
        {
          num: "04",
          title: "Interiors",
          body:
            "Quiet offices, composed salons, private interiors, places where each object is chosen with the exigence of a work.",
        },
      ],
      collabEyebrow: "— Collaborations",
      collabTitle: "Presentations, places, dialogues.",
      collabBody1:
        "Maison Dodji welcomes a limited number of collaborations with interior architects, decorators, private places, exceptional residential projects, artists and galleries selected as spaces of presentation. Certain horological actors may also be associated when the dialogue respects the spirit of the maison. Each dialogue is built over time, around the piece — not around the transaction.",
      collabBody2:
        "Private presentations take place in dedicated venues or directly within clients' interiors. The maison travels when the piece justifies it.",
      collabSpacesEyebrow: "— Spaces of collaboration",
      collabSpacesTitle: "Private staging.",
      collabSpacesBody:
        "Maison Dodji opens a space of collaboration with interior architects, decorators, galleries and private venues, in order to stage its pieces within denser, more inhabited environments — where horological furniture enters into dialogue with materials, works, light and use.",
      stagingNoteEyebrow: "— A light to come",
      stagingNote:
        "The current visuals present the pieces in an intentionally neutral light. Future collaborations will allow them to be set within denser interiors, expressing their relation to materials, works, furniture and places.",
      locations: [
        ["By appointment", "Private presentations"],
        ["On location", "By project"],
        ["By invitation", "Regular edition"],
      ],
      imageAlts: {
        sankara: "Sankara — horizontal presence",
        rosa: "Rosa — circular piece",
        quincy: "Quincy — density of the quartet",
      },
    },

    contact: {
      eyebrow: "— Private Presentation",
      byAppointment: "By appointment",
      title: "Write to the Maison",
      intro:
        "For any private presentation, acquisition or collaboration enquiry, please write to the Maison via the form below or directly at:",
      asideL1: "A relationship,",
      asideL2: "not a transaction.",

      processEyebrow: "— The process",
      steps: [
        {
          num: "01",
          title: "Initial conversation",
          body:
            "We respond within 48 hours. A first exchange allows us to understand your space, your intention, and the piece you have in mind.",
        },
        {
          num: "02",
          title: "Private presentation",
          body:
            "By appointment, in dedicated venues or directly within your interior when the project calls for it. The piece is presented in the context that will receive it.",
        },
        {
          num: "03",
          title: "Continued care",
          body:
            "Each piece is conceived, delivered and accompanied over time. The maison remains your sole interlocutor.",
        },
      ],

      asideEyebrow: "— Contact",
      writeEyebrow: "Write to the maison",

      form: {
        nom: "Name",
        nomPlaceholder: "Your name",
        email: "Email",
        emailPlaceholder: "you@address.com",
        ville: "City / Country",
        villePlaceholder: "Paris, France",
        objet: "Subject of inquiry",
        piece: "Piece of interest",
        pieceNoPreference: "— No preference",
        message: "Message",
        messagePlaceholder:
          "A few words on your project, your space, your intention…",
        note: "We will be in touch shortly.",
        submit: "Send the inquiry",
        submitting: "Sending…",
      },
      objects: [
        "Private presentation",
        "Information",
        "Specific project",
        "Collaboration",
      ],

      successEyebrow: "— Inquiry received",
      successTitle: "Thank you. The maison will be in touch soon.",
      successBody:
        "Your inquiry has been received by Maison Dodji. We will be in touch shortly to arrange a private presentation.",

      toastSuccess: "Your inquiry has been received by the maison.",
      toastErrorRequired: "Please complete the essential fields.",
      toastErrorGeneric: "Something went wrong. Please try again.",

      closingEyebrow: "— Maison Dodji",
      closingTitle: "Singular furniture pieces for automatic watches.",
      closingBody:
        "By appointment. The maison travels when the piece justifies it.",
    },
  },
};

export const getCopyright = (lang) => {
  const tpl = translations[lang].footer.copyright;
  return tpl.replace("{year}", String(new Date().getFullYear()));
};
